import axios, { AxiosPromise } from 'axios';
import configs from '../configs';
import { store } from '../Components/store/store';

// Create an axios instance for admin API requests
const adminAxios = axios.create({
  baseURL: `${configs.context}/admin`,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Access-Control-Allow-Origin': '*'
  }
});

// Add token to requests
adminAxios.interceptors.request.use(config => {
  const token = store.getState()?.data?.login?.value?.token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle responses and errors
adminAxios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Handle unauthorized (e.g., redirect to login)
      // You might want to dispatch a logout action here
      window.location.href = '/sign-in';
    }
    if (error.response?.status === 403) {
      // Handle forbidden (not a superadmin)
      window.location.href = '/dashboard';
    }
    return Promise.reject(error);
  }
);

// Admin API service
class AdminApiClass {
  // Dashboard
  getStatistics(): AxiosPromise<any> {
    return adminAxios.get('/statistics');
  }
  
  // User management
  getUsers(params = {}): AxiosPromise<any> {
    return adminAxios.get('/users', { params });
  }
  
  getUser(userId: number): AxiosPromise<any> {
    return adminAxios.get(`/users/${userId}`);
  }
  
  createUser(userData: any): AxiosPromise<any> {
    return adminAxios.post('/users', userData);
  }
  
  updateUser(userId: number, userData: any): AxiosPromise<any> {
    return adminAxios.put(`/users/${userId}`, userData);
  }
  
  deleteUser(userId: number): AxiosPromise<any> {
    return adminAxios.delete(`/users/${userId}`);
  }
  
  changeUserStatus(userId: number): AxiosPromise<any> {
    return adminAxios.patch(`/users/${userId}/status`);
  }
  
  changeUserRole(userId: number, role: string): AxiosPromise<any> {
    return adminAxios.patch(`/users/${userId}/role`, { role });
  }
  
  // User-related data
  getUserMedia(userId: number, params = {}): AxiosPromise<any> {
    return adminAxios.get(`/users/${userId}/media`, { params });
  }
  
  getUserTransactions(userId: number, params = {}): AxiosPromise<any> {
    return adminAxios.get(`/users/${userId}/transactions`, { params });
  }
  
  getUserRecipients(userId: number, params = {}): AxiosPromise<any> {
    return adminAxios.get(`/users/${userId}/recipients`, { params });
  }
  
  getUserActivity(userId: number): AxiosPromise<any> {
    return adminAxios.get(`/users/${userId}/activity`);
  }

  // Plans management
  // Access (promo) codes
  getPromoCodes(params = {}): AxiosPromise<any> {
    return adminAxios.get('/promo-codes', { params });
  }

  createPromoCode(data: any): AxiosPromise<any> {
    return adminAxios.post('/promo-codes', data);
  }

  togglePromoCode(codeId: number): AxiosPromise<any> {
    return adminAxios.patch(`/promo-codes/${codeId}/toggle`);
  }

  deletePromoCode(codeId: number): AxiosPromise<any> {
    return adminAxios.delete(`/promo-codes/${codeId}`);
  }

  getPlans(): AxiosPromise<any> {
    return adminAxios.get('/plans');
  }

  createPlan(planData: any): AxiosPromise<any> {
    return adminAxios.post('/plans', planData);
  }

  updatePlan(planId: number, planData: any): AxiosPromise<any> {
    return adminAxios.put(`/plans/${planId}`, planData);
  }

  deletePlan(planId: number): AxiosPromise<any> {
    return adminAxios.delete(`/plans/${planId}`);
  }

  // Media management
  getAllMedia(params = {}): AxiosPromise<any> {
    return adminAxios.get('/media', { params });
  }

  deleteMedia(mediaId: number): AxiosPromise<any> {
    return adminAxios.delete(`/media/${mediaId}`);
  }

  // Reports
  getSystemStats(): AxiosPromise<any> {
    return adminAxios.get('/reports/system');
  }

  getUserStats(): AxiosPromise<any> {
    return adminAxios.get('/reports/users');
  }

  getMediaStats(): AxiosPromise<any> {
    return adminAxios.get('/reports/media');
  }

  getRevenueStats(): AxiosPromise<any> {
    return adminAxios.get('/reports/revenue');
  }

  getUserReferrals(userId:any): AxiosPromise<any> {
    return adminAxios.get(`/users/${userId}/referrals`);
  }

  exportReport(reportType: string, timeRange: string): AxiosPromise<any> {
    return adminAxios.get(`/reports/export/${reportType}`, {
      params: { time_range: timeRange },
      responseType: 'blob'
    });
  }

  // Profile management
  getProfile(): AxiosPromise<any> {
    return adminAxios.get('/profile');
  }
  
  updateProfile(profileData: any): AxiosPromise<any> {
    const formData = new FormData();
    
    // Append text fields
    Object.keys(profileData).forEach(key => {
      if (key !== 'avatar') {
        formData.append(key, profileData[key]);
      }
    });
    
    // Append avatar if it exists
    if (profileData.avatar && profileData.avatar instanceof File) {
      formData.append('avatar', profileData.avatar);
    }
    
    return adminAxios.post('/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }
  
  changePassword(passwordData: {
    current_password: string;
    new_password: string;
    new_password_confirmation: string;
  }): AxiosPromise<any> {
    return adminAxios.post('/profile/change-password', passwordData);
  }
}

 


// Create a singleton instance
const AdminApi = new AdminApiClass();

export default AdminApi;